"use client";

import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

import type { DashboardTimeRangeSelection } from "./widget-config";

export type DashboardRuntimeValue = {
  /** When set, overrides each widget's `config.time_range` for data queries. */
  globalTimeRange: DashboardTimeRangeSelection | null;
  setGlobalTimeRange: (value: DashboardTimeRangeSelection | null) => void;
  /** Semantic alert rule bindings with at least one active alert (see GET /alerts/summary/active). */
  activeAlertFingerprints: ReadonlySet<string>;
};

const DashboardRuntimeContext = createContext<DashboardRuntimeValue | null>(null);

export function DashboardRuntimeProvider({
  children,
  activeAlertFingerprints,
}: {
  children: ReactNode;
  activeAlertFingerprints?: ReadonlySet<string>;
}) {
  const [globalTimeRange, setGlobalTimeRange] = useState<DashboardTimeRangeSelection | null>(null);
  const fps = activeAlertFingerprints ?? new Set<string>();
  const value = useMemo(
    () => ({
      globalTimeRange,
      setGlobalTimeRange,
      activeAlertFingerprints: fps,
    }),
    [globalTimeRange, fps],
  );
  return <DashboardRuntimeContext.Provider value={value}>{children}</DashboardRuntimeContext.Provider>;
}

export function useDashboardRuntime(): DashboardRuntimeValue | null {
  return useContext(DashboardRuntimeContext);
}
